import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import './ui.css';

export function IconButton({
  icon,
  label,
  onClick,
  pressed,
  disabled,
  variant = 'plain',
  className,
  children,
  id,
}: {
  icon?: IconName;
  label: string;
  onClick?: () => void;
  pressed?: boolean;
  disabled?: boolean;
  variant?: 'plain' | 'circle' | 'filled' | 'white';
  className?: string;
  children?: ReactNode;
  id?: string;
}) {
  return (
    <button
      id={id}
      type="button"
      className={`icon-btn v-${variant} ${pressed ? 'is-pressed' : ''} ${className ?? ''}`}
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
    >
      {children ?? (icon && <Icon name={icon} />)}
    </button>
  );
}

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

/** Bottom sheet (reference image picker look): white, rounded top, grabber, scrim, Esc/scrim to close, focus trap. */
export function Sheet({
  open,
  onClose,
  title,
  children,
  header,
  className,
  tall,
  hideTitle,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  header?: ReactNode;
  className?: string;
  tall?: boolean;
  hideTitle?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const el = ref.current;
    const first = el?.querySelector<HTMLElement>('[data-autofocus]') ?? el?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      } else if (e.key === 'Tab' && el) {
        const items = [...el.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((x) => x.offsetParent !== null);
        if (!items.length) return;
        const a = items[0];
        const z = items[items.length - 1];
        if (e.shiftKey && document.activeElement === a) {
          e.preventDefault();
          z.focus();
        } else if (!e.shiftKey && document.activeElement === z) {
          e.preventDefault();
          a.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      prev?.focus?.({ preventScroll: true });
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="sheet-root no-print">
      <div className="sheet-scrim" onClick={onClose} aria-hidden="true" />
      <div ref={ref} className={`sheet ${tall ? 'is-tall' : ''} ${className ?? ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="sheet-grabber" aria-hidden="true" />
        <div className="sheet-head">
          <IconButton icon="close" label="Close" variant="white" onClick={onClose} className="sheet-close" />
          <h2 id={titleId} className={hideTitle ? 'sr-only' : 'sheet-title'}>
            {title}
          </h2>
          {header}
        </div>
        <div className="sheet-body">{children}</div>
      </div>
    </div>
  );
}

export function Segmented<T extends string>({ value, options, onChange, label }: { value: T; options: { value: T; label: string }[]; onChange: (v: T) => void; label: string }) {
  return (
    <div className="segmented" role="tablist" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" role="tab" aria-selected={value === o.value} className={value === o.value ? 'is-on' : ''} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled} className={`switch ${checked ? 'is-on' : ''}`} onClick={() => onChange(!checked)}>
      <span className="switch-knob" />
    </button>
  );
}

export function Group({ title, children, footer }: { title?: string; children: ReactNode; footer?: ReactNode }) {
  return (
    <section className="group">
      {title && <h3 className="group-title">{title}</h3>}
      <div className="group-body">{children}</div>
      {footer && <p className="group-footer">{footer}</p>}
    </section>
  );
}

export function Row({ label, detail, children, onClick, icon, danger, htmlFor }: { label: ReactNode; detail?: ReactNode; children?: ReactNode; onClick?: () => void; icon?: IconName; danger?: boolean; htmlFor?: string }) {
  const content = (
    <>
      {icon && <Icon name={icon} size={20} className="row-icon" />}
      <span className="row-label">
        {htmlFor ? <label htmlFor={htmlFor}>{label}</label> : label}
        {detail && <span className="row-detail">{detail}</span>}
      </span>
      {children && <span className="row-control">{children}</span>}
      {onClick && !children && <Icon name="forward" size={18} className="row-chevron" />}
    </>
  );
  return onClick ? (
    <button type="button" className={`row is-button ${danger ? 'is-danger' : ''}`} onClick={onClick}>
      {content}
    </button>
  ) : (
    <div className={`row ${danger ? 'is-danger' : ''}`}>{content}</div>
  );
}

export function ScreenHeader({ title, onBack, actions, backLabel = 'Back' }: { title: string; onBack?: () => void; actions?: ReactNode; backLabel?: string }) {
  return (
    <header className="screen-header">
      <div className="screen-header-side">{onBack && <IconButton icon="back" label={backLabel} onClick={onBack} />}</div>
      <h1 className="screen-title">{title}</h1>
      <div className="screen-header-side is-end">{actions}</div>
    </header>
  );
}

// ------------------------------------------------------------- confirm dialog

interface ConfirmRequest {
  title: string;
  message?: string;
  confirmLabel?: string;
  danger?: boolean;
  resolve: (ok: boolean) => void;
}

let pushConfirm: ((r: ConfirmRequest) => void) | null = null;

export function confirmDialog(opts: Omit<ConfirmRequest, 'resolve'>): Promise<boolean> {
  return new Promise((resolve) => {
    if (!pushConfirm) {
      resolve(window.confirm(`${opts.title}${opts.message ? `\n\n${opts.message}` : ''}`));
      return;
    }
    pushConfirm({ ...opts, resolve });
  });
}

export function ConfirmHost() {
  const [req, setReq] = useState<ConfirmRequest | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    pushConfirm = setReq;
    return () => {
      pushConfirm = null;
    };
  }, []);
  useEffect(() => {
    if (!req) return;
    btn.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        req.resolve(false);
        setReq(null);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [req]);
  if (!req) return null;
  const done = (ok: boolean) => {
    req.resolve(ok);
    setReq(null);
  };
  return (
    <div className="confirm-root" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title" aria-describedby={req.message ? 'confirm-msg' : undefined}>
      <div className="sheet-scrim" onClick={() => done(false)} />
      <div className="confirm">
        <h2 id="confirm-title">{req.title}</h2>
        {req.message && <p id="confirm-msg">{req.message}</p>}
        <div className="confirm-actions">
          <button type="button" className="btn" onClick={() => done(false)}>
            Cancel
          </button>
          <button ref={btn} type="button" className={`btn ${req.danger ? 'is-danger' : 'is-primary'}`} onClick={() => done(true)}>
            {req.confirmLabel ?? 'OK'}
          </button>
        </div>
      </div>
    </div>
  );
}
